import { useState } from 'react'

import { useDeleteTeam } from '@/hooks/useDeleteTeam'
import { useOrgTeams } from '@/hooks/useOrgTeams'
import { AddTeamMember } from '@/components/AddTeamMember'
import { TeamItem } from '@/components/TeamItem'
import { Spinner } from '@/components/Spinner'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { Modal } from '@/components/Modal'
import { useToast } from '@/hooks/Toast'
import { Team } from '@/types'

import s from './Teams.module.css'

type Props = {
  organizationId?: string
}

export const Teams = ({ organizationId }: Props) => {
  const { data: teams } = useOrgTeams(organizationId)
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [inputValue, setInputValue] = useState('')
  const deleteTeamMutation = useDeleteTeam()
  const toast = useToast()

  const handleDeleteTeam = async () => {
    try {
      await deleteTeamMutation.mutateAsync({
        teamId: deletingTeam!.id,
      })
      setDeletingTeam(null)
    } catch (error) {
      toast.error('Something went wrong. Try again?')
    }
  }

  if (!teams) {
    return <Spinner />
  }

  if (teams.length === 0) {
    return (
      <div className={s.emptyState}>
        <div className={s.emptyStateTitle}>No teams</div>
        <p>You're not a member of any team yet.</p>
      </div>
    )
  }

  return (
    <div className={s.container}>
      {teams?.map((team) => (
        <TeamItem
          key={team.id}
          team={team}
          onAddMember={() => setEditingTeam(team)}
          onDelete={() => setDeletingTeam(team)}
        />
      ))}

      <Modal
        title="Add Members"
        description={`Choose members from your organization to add to the team "${editingTeam?.name}".`}
        open={!!editingTeam}
        onClose={() => {
          setEditingTeam(null)
        }}
      >
        {editingTeam ? (
          <AddTeamMember
            teamId={editingTeam!.id}
            onClose={() => setEditingTeam(null)}
          />
        ) : (
          <Spinner />
        )}
      </Modal>

      <Modal
        title="Delete Team"
        description={`Deleting a team will delete all its links. Type "${deletingTeam?.name}" to confirm.`}
        open={!!deletingTeam}
        onClose={() => {
          setDeletingTeam(null)
          setInputValue('')
        }}
      >
        <div className={s.input}>
          <Input
            name="teamName"
            placeholder="Team name"
            onChange={(e) => setInputValue(e.target.value)}
            fullWidth
          />
        </div>
        <Button
          variant="destructive"
          disabled={
            inputValue !== deletingTeam?.name || deleteTeamMutation.isPending
          }
          onClick={handleDeleteTeam}
        >
          Delete
        </Button>
      </Modal>
    </div>
  )
}
